import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { fetchCaseStudies } from "../services/API";
import { Helmet } from "@dr.pogodin/react-helmet";

export default function CaseStudies() {
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 9;

  const [caseStudies, setCaseStudies] = useState([]);
  const [filteredStudies, setFilteredStudies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [regions, setRegions] = useState([]);

  const [filters, setFilters] = useState({
    category: [],
    platform: [],
    industry: [],
    region: [],
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [openSections, setOpenSections] = useState({
    category: true,
    platform: false,
    industry: false,
    region: false,
  });

  const backendUrl = import.meta.env.VITE_BACKEND_API_URL;
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Case Studies | Web Hosting & Server Success Stories";
    fetchCaseStudies().then((data) => {
      setCaseStudies(data);
      setFilteredStudies(data);

      const extractNames = (val) => {
        if (!val) return [];
        if (Array.isArray(val)) return val.map((v) => v.name || v);
        if (typeof val === "object") return [val.name || JSON.stringify(val)];
        return [String(val)];
      };

      const uniqueCategories = [
        ...new Set(
          data.flatMap((cs) => extractNames(cs.category)).filter(Boolean)
        ),
      ];
      setCategories(uniqueCategories);

      const uniquePlatforms = [
        ...new Set(
          data.flatMap((cs) => extractNames(cs.platform)).filter(Boolean)
        ),
      ];
      setPlatforms(uniquePlatforms);

      const uniqueIndustries = [
        ...new Set(
          data.flatMap((cs) => extractNames(cs.industry)).filter(Boolean)
        ),
      ];
      setIndustries(uniqueIndustries);

      const uniqueRegions = [
        ...new Set(
          data.flatMap((cs) => extractNames(cs.region)).filter(Boolean)
        ),
      ];
      setRegions(uniqueRegions);
    });
  }, []);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const category = queryParams.get("category");
    const platform = queryParams.get("platform");
    const industry = queryParams.get("industry");
    const region = queryParams.get("region");

    setFilters({
      category: category ? [category] : [],
      platform: platform ? [platform] : [],
      industry: industry ? [industry] : [],
      region: region ? [region] : [],
    });
  }, [location.search]);

  useEffect(() => {
    let filtered = [...caseStudies];

    if (searchTerm) {
      filtered = filtered.filter(
        (cs) =>
          cs.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          cs.category?.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    Object.entries(filters).forEach(([key, values]) => {
      if (values.length > 0) {
        filtered = filtered.filter((cs) => {
          const field = cs[key];
          if (Array.isArray(field)) {
            return field.some((f) => values.includes(f));
          }
          return values.includes(field?.name || field);
        });
      }
    });

    const total = filtered.length;
    setTotalPages(Math.ceil(total / itemsPerPage));

    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    const paginated = filtered.slice(start, end);

    setFilteredStudies(paginated);
  }, [filters, searchTerm, caseStudies, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm]);

  const toggleSection = (section) =>
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));

  const handleCheckboxChange = (type, value) => {
    setFilters((prev) => {
      const alreadySelected = prev[type].includes(value);
      const updated = alreadySelected
        ? prev[type].filter((v) => v !== value)
        : [...prev[type], value];

      const queryParams = new URLSearchParams(location.search);
      queryParams.set(type, updated.join(","));
      navigate(`?${queryParams.toString()}`);

      return { ...prev, [type]: updated };
    });
  };

  const clearAll = () => {
    setFilters({ category: [], platform: [], industry: [], region: [] });
    setSearchTerm("");
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    const pages = [];
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <li key={i} className={`page-item me-2 case-study-pagination ${i === currentPage ? "active" : ""}`}>
          <button className="page-link" onClick={() => handlePageChange(i)}> {i} </button>
        </li>
      );
    }

    return (
      <nav className="mt-3">
        <ul className="pagination justify-content-center flex-wrap">
          {currentPage > 1 && (
            <li className="page-item me-2">
              <button className="page-link" onClick={() => handlePageChange(currentPage - 1)} > Prev </button>
            </li>
          )}

          {startPage > 1 && (
            <>
              <li className="page-item me-2">
                <button className="page-link" onClick={() => handlePageChange(1)}> 1 </button>
              </li>
              <li className="page-item me-2 disabled"> <span className="page-link">...</span> </li>
            </>
          )}

          {pages}

          {endPage < totalPages && (
            <>
              <li className="page-item me-2 disabled"> <span className="page-link">...</span> </li>
              <li className="page-item me-2">
                <button className="page-link" onClick={() => handlePageChange(totalPages)} >
                  {totalPages}
                </button>
              </li>
            </>
          )}

          {currentPage < totalPages && (
            <li className="page-item me-2">
             <button className="page-link" onClick={() => handlePageChange(currentPage + 1)} > Next </button>
            </li>
          )}
        </ul>
      </nav>
    );
  };

  return (
    <>
      <Helmet>
          <title>Case Studies | Web Hosting & Server Success Stories</title>
          <meta name="description" content="Explore real client case studies showcasing how our web hosting, VPS, dedicated servers, and GPU solutions power high-performance businesses." />
          <meta name="keywords" content="web hosting case studies, server case study, dedicated server, VPS hosting, GPU server case study, cloud hosting, Infinitive Host" />
          <meta property="og:url" content={`${import.meta.env.VITE_FRONTEND_URL}/windows-vps`} />
          <link rel="canonical" href={`${import.meta.env.VITE_FRONTEND_WWW_URL}/windows-vps`} />
          <meta property="og:title" content="Hosting & Server Case Studies – Real Success Stories" />
          <meta property="og:description" content="See how businesses grow using our web hosting, dedicated servers, VPS, and GPU infrastructure through real-world success stories." />
      </Helmet>

      <section className="banner-bg bg-dark linux-reseller-banner">
        <div className="container">
          <div className="row align-items-center">
              <div className="col-lg-7 col-xl-6">
                <span className="fs-18 fw-semibold text-primary mb-2 sal-animate" data-sal="slide-up" data-sal-duration="1500" data-sal-delay="300" data-sal-easing="ease-in-out-sine">From Strategy to Success</span>
                <h1 className="text-white mb-4 sal-animate" data-sal="slide-up" data-sal-duration="1000" data-sal-delay="300" data-sal-easing="ease-in-out-sine">From Disappointment to Contentment: Our Clients Journey</h1>
                <p className="mb-8 text-white max-text-72 text-opacity-75 sal-animate" data-sal="slide-up" data-sal-duration="1500" data-sal-delay="300" data-sal-easing="ease-in-out-sine">Every customer story always begins with a lot of struggle-see how we help customers move toward growth, trust, and satisfaction.</p>
              </div>
              <div className="col-lg-5 col-xl-6 text-lg-end ipad-mt">
                <img alt="image" className="img-fluid sal-animate full-width-img" data-sal="fade" data-sal-duration="1500" data-sal-delay="300" data-sal-easing="ease-in-out-sine" src="/images/flip-the-script-from.webp" />
              </div>
          </div>
        </div>
      </section>

      <div className="container-fluid pt-100 pb-100">
        <div className="row">
          <div className="col-lg-3 mb-4">
            <div className="filter-card border rounded shadow-sm">
              <div className="bg-primary d-flex justify-content-between p-3 rounded-top">
                <h6 className="text-white mt-2">Filters</h6>
                <button className="btn btn-sm btn-secondary clearAll" onClick={clearAll} > Clear All </button>
              </div>

              <div className="p-3 bg-light">
                <div className="position-relative mb-3">
                  <input type="text" className="form-control" placeholder="Search here..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                  <i className="fa-solid fa-magnifying-glass position-absolute top-50 end-0 translate-middle-y me-3 text-muted"></i>
                </div>

                {[
                  { label: "Category", key: "category", data: categories },
                  { label: "Platform", key: "platform", data: platforms },
                  { label: "Industry", key: "industry", data: industries },
                  { label: "Region", key: "region", data: regions },
                ].map(({ label, key, data }) => (
                  <div className="filter-section mb-2" key={key}>
                    <button className="w-100 d-flex justify-content-between align-items-center bg-secondary border-2 py-2 px-3" onClick={() => toggleSection(key)} >
                      <span>{label}</span>
                      <i className={`fa-solid ${ openSections[key] ? "fa-minus" : "fa-plus" }`}></i>
                    </button>

                    {openSections[key] && (
                      <div className="p-2 border border-top-0 bg-white">
                        {data.length > 0 ? (
                          data.map((item, index) => (
                            <div className="form-check" key={index}>
                              <input type="checkbox" className="form-check-input" id={`${key}-${index}`} checked={filters[key].includes(item)} onChange={() => handleCheckboxChange(key, item)} />
                              <label className="form-check-label" htmlFor={`${key}-${index}`} >
                                {item}
                              </label>
                            </div>
                          ))
                        ) : (
                          <small className="text-muted"> No options available </small>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="col-lg-9">
            <div className="row px-4">
              {filteredStudies.length === 0 ? (
                <p className="text-center mt-4 fs-5 text-muted"> No case studies found. </p>
              ) : (
                filteredStudies.map((cs) => (
                  <div className="col-lg-4 col-sm-6 mb-4" key={cs._id}>
                    <div className="card shadow h-100">
                      <div className="card-body">
                        <div className="position-relative">
                          <div className="caseThumbImg">
                            <Link to={`/case-study/${cs.pageUrl}`}>
                            <img src={`${backendUrl}/uploads/case-study-tile/images/${cs.caseStudyThumbForMobile}`} alt={cs.title} className="img-fluid" />
                            </Link>
                          </div>
                          <span className="category-badge position-absolute top-0 end-0 m-2 bg-primary text-white px-2 py-1 rounded">
                            {cs.category?.name || cs.category || (Array.isArray(cs.category) ? cs.category.join(", ") : "")}
                          </span>
                        </div>

                        <h5 className="mt-3">
                          <Link to={`/case-study/${cs.pageUrl}`} className="text-dark text-decoration-none fs-6" >
                            {cs.title}
                          </Link>
                        </h5>

                        <p>
                          {cs.shortDescription.length > 100
                            ? cs.shortDescription.slice(0, 100) + "..."
                            : cs.shortDescription}
                        </p>

                        <div className="text-end">
                          <Link to={`/case-study/${cs.pageUrl}`} className="btn btn-primary btn-arrow btn-lg fs-14 fw-medium rounded m-auto btn-height">
                            <span className="d-inline-block me-1 flex-shrink-0">
                              <i className="las la-comments"></i>
                            </span>
                            <span className="btn-arrow__text">Read More
                              <span className="btn-arrow__icon">
                                <i className="fa-solid fa-arrow-right"></i>
                              </span>
                            </span>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
            {renderPagination()}
          </div>
        </div>
      </div>
    </>
  );
}
